import React, { useCallback, useEffect, useRef } from "react";
import styles from "./index.module.scss";
import classnames from "classnames";
import { ScrollPositionType, ScrollLabel } from "constants/scrollPositionType";
import { EventBusType } from "events/EventBus";
import { EventBus } from "events/EventBus";
import { ReaderToolsEvent } from "events/EventTypes";
import { ReaderZoomType } from "constants/ReaderTools";
import { useStore, StoreTypes } from "context";
import { ScrollPositionButtonStateType } from "constants/scrollPositionType";
import { ScrollCoordinate } from "constants/scrollPositionType";
import * as types from "constants/actionTypes";
import { ModalComponentType } from "components/common/Modal/ModalComponentGenerator";
import { ExtendedContentType, getPaginationConfig } from "../ExtendedContentContainer";

const buttonListData = [
  {
    label: ScrollLabel.Next,
    value: ScrollPositionType.Next,
    stopSign: ScrollPositionButtonStateType.Bottom,
  },
  {
    label: ScrollLabel.Prev,
    value: ScrollPositionType.Prev,
    stopSign: ScrollPositionButtonStateType.Head,
  },
];

export const ExtendedContentScrollPositionButton = ({ extendedContentType }) => {
  const [{ scrollPositionButtonState, currentPage, totalPages, shouldSplited }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);
  // 觸控裝置上一次點擊會讓 onMouseDown 的 handler 跑兩次，currentPage 因此每次 +2
  // （3 頁時 1→3、6 頁時 1→3→5→6，最後一步被 Math.min 夾住）。
  // mousedown 上鎖、mouseup（ScrollPositionType.None）解鎖，一次點擊只生效一次。
  //
  // ⚠️ 這個 guard 是 NBK-1519 的修正，曾在 f6a9017a5（WordGridModal cherrypick）
  //    被夾帶的舊版檔案整段蓋掉而復發成 NBK-1670。改動這個檔案時請確認它還在。
  const pageChangeGuardRef = useRef(false);

  // 根據 currentPage 和 totalPages 計算並更新按鈕狀態
  useEffect(() => {
    // 該類型自行管理按鈕狀態，不在此覆蓋
    const { selfManagedPagination } = getPaginationConfig(extendedContentType);
    if (selfManagedPagination) return;

    let buttonState;
    if (totalPages <= 1) {
      buttonState = ScrollPositionButtonStateType.None;
    } else if (currentPage === 0) {
      buttonState = ScrollPositionButtonStateType.Head;
    } else if (currentPage === totalPages - 1) {
      buttonState = ScrollPositionButtonStateType.Bottom;
    } else {
      buttonState = ScrollPositionButtonStateType.Middle;
    }

    // 只在狀態實際改變時才更新
    if (buttonState !== scrollPositionButtonState) {
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_SCROLL_POSITION_BUTTON_STATE,
        payload: { scrollPositionButtonState: buttonState },
      });
    }
  }, [currentPage, totalPages, scrollPositionButtonState, extendedContentDispatch, extendedContentType]);

  // split 分頁模式下換頁時，將縮放（四格放大、滾輪放大、區域放大）與位移歸零，
  // 避免上一頁的放大狀態殘留到新頁。沿用工具列「還原」的 OriginZoom 事件，
  // 由 ExtendedContentContainer 統一把 scale/offset 重置為預設值。
  const prevPageRef = useRef(currentPage);
  useEffect(() => {
    if (prevPageRef.current === currentPage) return;
    prevPageRef.current = currentPage;
    if (!shouldSplited) return;

    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderToolsEvent.SetReaderZoomEvent,
      payload: { type: ReaderZoomType.OriginZoom },
    });
  }, [currentPage, shouldSplited]);

  const changePopWindowScrollPositionHandler = useCallback((scrollPosition) => {
    // 這個事件只 emit 一次。原本 Next/Prev 會重複 emit 兩次，下游得靠
    // WordGridSubContentPanel 的 lastNavRef 去重才不出事（NBK-1670）。
    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionEvent,
      payload: { scrollPosition },
    });

    // ScrollPositionType.None 由 onMouseUp 傳入：解鎖，不換頁
    if (scrollPosition === ScrollPositionType.None) {
      pageChangeGuardRef.current = false;
      return;
    }

    if (pageChangeGuardRef.current) return;
    pageChangeGuardRef.current = true;

    if (scrollPosition === ScrollPositionType.Next) {
      const nextPage = Math.min(currentPage + 1, Math.max(totalPages - 1, 0));
      if (nextPage !== currentPage) {
        extendedContentDispatch({
          type: types.SET_EXTENDED_CONTENT_CURRENT_PAGE,
          payload: { currentPage: nextPage },
        });
      }
    } else if (scrollPosition === ScrollPositionType.Prev) {
      const prevPage = Math.max(currentPage - 1, 0);
      if (prevPage !== currentPage) {
        extendedContentDispatch({
          type: types.SET_EXTENDED_CONTENT_CURRENT_PAGE,
          payload: { currentPage: prevPage },
        });
      }
    }
  }, [currentPage, totalPages, extendedContentDispatch]);

  const { showPageInfo } = getPaginationConfig(extendedContentType);

  return (
    <div className={styles.extendedContentScrollPositionButton}>
      {buttonListData.filter(b => {return (extendedContentType === ModalComponentType.WordGrid
      || extendedContentType === ModalComponentType.Hearing
      || extendedContentType === ExtendedContentType.WordGridSubContent) ? b.value === ScrollPositionType.Next : b.value === ScrollPositionType.Prev}).map((item) => {
        const disableCheck =
          scrollPositionButtonState === item.stopSign ||
          scrollPositionButtonState === ScrollPositionButtonStateType.None;
        return (
          <div
            className={classnames(styles.button, {
              [styles.disabled]: disableCheck,
            })}
            style={{ backgroundColor: disableCheck && "#999" }}
            key={item.value}
            onMouseDown={() => {
              if (disableCheck) return;
              return changePopWindowScrollPositionHandler(item.value);
            }}
            onMouseUp={() =>
              changePopWindowScrollPositionHandler(ScrollPositionType.None)
            }
          >
            {item.label}
          </div>
        );
      })}
      {showPageInfo && <div className={styles.pageInfo} aria-label={`page ${currentPage + 1} of ${totalPages}`}>
        {`${currentPage + 1}/${totalPages}`}
      </div>}
      {buttonListData.filter(b => {return (extendedContentType === ModalComponentType.WordGrid
      || extendedContentType === ModalComponentType.Hearing
      || extendedContentType === ExtendedContentType.WordGridSubContent) ? b.value === ScrollPositionType.Prev : b.value === ScrollPositionType.Next}).map((item) => {
        const disableCheck =
          scrollPositionButtonState === item.stopSign ||
          scrollPositionButtonState === ScrollPositionButtonStateType.None;
        return (
          <div
            className={classnames(styles.button, {
              [styles.disabled]: disableCheck,
            })}
            style={{ backgroundColor: disableCheck && "#999" }}
            key={item.value}
            onMouseDown={() => {
              if (disableCheck) return;
              return changePopWindowScrollPositionHandler(item.value);
            }}
            onMouseUp={() =>
              changePopWindowScrollPositionHandler(ScrollPositionType.None)
            }
          >
            {item.label}
          </div>
        );
      })}
    </div>
  );
};

// 記錄目前位置與上次位置，判斷是否擦掉劃記與按鈕顏色
export const scrollPositionButtonBehavior = ({
  currentScroll,
  lastScroll,
  coordinate,
}) => {
  const eventBusType = EventBusType.ExtendedContent;
  if (coordinate === ScrollCoordinate.X) {
    if (currentScroll !== lastScroll && (currentScroll < 0 || lastScroll < 0)) {
      EventBus.emit({
        eventBusType,
        event: ReaderToolsEvent.ClickEraseAllEvent,
      });
    }
  } else if (coordinate === ScrollCoordinate.Y) {
    if (currentScroll !== lastScroll && (currentScroll > 0 || lastScroll > 0)) {
      EventBus.emit({
        eventBusType,
        event: ReaderToolsEvent.ClickEraseAllEvent,
      });
    }
  }
};

export const ScrollButtonStateBehavior = (state) => {
  const eventBusType = EventBusType.ExtendedContent;
  if (state === ScrollPositionButtonStateType.Head) {
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionButtonStateEvent,
      payload: {
        scrollPositionButtonState: ScrollPositionButtonStateType.Head,
      },
    });
  } else if (state === ScrollPositionButtonStateType.Bottom) {
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionButtonStateEvent,
      payload: {
        scrollPositionButtonState: ScrollPositionButtonStateType.Bottom,
      },
    });
  } else if (state === ScrollPositionButtonStateType.None) {
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionButtonStateEvent,
      payload: {
        scrollPositionButtonState: ScrollPositionButtonStateType.None,
      },
    });
  } else {
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ChangePopWindowScrollPositionButtonStateEvent,
      payload: {
        scrollPositionButtonState: ScrollPositionButtonStateType.Middle,
      },
    });
  }
};
